import { produce } from 'immer';
import type { StoreApi } from 'zustand';
import type { MapLayer } from '@/lib/db-row-types';

// Map-Layer relationship slice
export type MapLayerState = {
  mapLayersList: MapLayer[];
  setMapLayers: (mapLayers: MapLayer[]) => void;
  addMapLayer: (mapLayer: MapLayer) => void;
  removeMapLayerById: (id: number) => void;
  updateMapLayer: (mapLayer: MapLayer) => void;
  clearMapLayers: () => void;
  handleMapLayerVisibilityChange: (mapLayer: MapLayer) => void;
  handleMapLayerStyleChange: (mapLayer: MapLayer) => void;
};

export const createMapLayerSlice = (set: StoreApi<any>['setState']) => ({
  mapLayersList: [],
  setMapLayers: (mapLayers: MapLayer[]) =>
    set(
      produce((draft: MapLayerState) => {
        draft.mapLayersList = mapLayers;
      })
    ),
  updateMapLayer: (mapLayer: MapLayer) =>
    set(
      produce((draft: MapLayerState) => {
        const index = draft.mapLayersList.findIndex(ml => ml.id === mapLayer.id);
        if (index !== -1) {
          draft.mapLayersList[index] = mapLayer;
        }
      })
    ),
  addMapLayer: (ml: MapLayer) =>
    set(
      produce((draft: MapLayerState) => {
        const existingIndex = draft.mapLayersList.findIndex(existing => existing.id === ml.id);
        if (existingIndex === -1) {
          draft.mapLayersList.push(ml);
        }
      })
    ),
  removeMapLayerById: (id: number) =>
    set(
      produce((draft: MapLayerState) => {
        const index = draft.mapLayersList.findIndex(ml => ml.id === id);
        if (index !== -1) {
          draft.mapLayersList.splice(index, 1);
        }
      })
    ),
  clearMapLayers: () =>
    set(
      produce((draft: MapLayerState) => {
        draft.mapLayersList = [];
      })
    ),
  handleMapLayerVisibilityChange: (mapLayer: MapLayer) =>
    set(
      produce((draft: MapLayerState) => {
        const currentMapLayer = draft.mapLayersList.find(ml => ml.id === mapLayer.id);

        // Only update if this is a meaningful change
        // We check if group membership changed (which affects visibility sync)
        // Note: updating mapbox_style_data causes infinite loop updates, so we need
        // to fix that before enabling full map layer updates via realtime
        if (currentMapLayer && currentMapLayer.group_id !== mapLayer.group_id) {
          const index = draft.mapLayersList.findIndex(ml => ml.id === mapLayer.id);
          if (index !== -1) {
            draft.mapLayersList[index] = mapLayer;
          }
        }
      })
    ),
  handleMapLayerStyleChange: (mapLayer: MapLayer) =>
    set(
      produce((draft: MapLayerState) => {
        const index = draft.mapLayersList.findIndex(ml => ml.id === mapLayer.id);
        if (index !== -1) {
          draft.mapLayersList[index] = mapLayer;
        }
      })
    ),
});
